import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isCopilotSearchAnswerEnabled } from 'owa-copilot-settings-utils';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isMailboxSharedOrDelegateUserMailbox } from 'owa-account-shared-mailbox-utils';
import { isBizchatSidepaneEnabled } from 'owa-bizchat-shared/lib/utils/isBizchatSidepaneEnabled';
import { isCohortAISKU } from 'owa-bizchat-shared/lib/utils/isCohortAISKU';
import { UserCohort } from 'owa-bizchat-types/lib/UserCohort';
import { getUserCohort } from 'owa-sidepane-bizchat-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

/**
 * Checks if the core Search Plus Chat prerequisites are met for the given mailbox.
 * This includes AI licensing, mailbox type validation, and base feature flags.
 */
export function isSearchPlusChatFeatureEnabled(mailboxInfo?: MailboxInfo): boolean {
    const targetMailboxInfo = mailboxInfo || getModuleContextMailboxInfo();

    // Get user cohort information
    const userCohort = getUserCohort(targetMailboxInfo);
    const isConsumerUser = isConsumer(undefined, targetMailboxInfo);

    // Case 1: Default - User has AI SKU (isCohortAISKU)
    const hasAISKU = !!isCohortAISKU(targetMailboxInfo);

    // Case 2: Starter SKU - Non-consumer starter user with feature flag enabled
    const isStarterUser = !isConsumerUser && userCohort === UserCohort.BCWAF;
    const hasStarterSKUAccess =
        isGlobalFeatureEnabled('sea-searchPlusChat-enableForStarterSKU') && isStarterUser;

    // Case 3: Consumer AI - Consumer user with AI cohort
    const isConsumerWithAI =
        isConsumerUser && (userCohort === UserCohort.BCWAF || userCohort === UserCohort.BCAIS);

    // Check if BizChat and AI features are enabled (one of the three cases must be true)
    const isChatAndAIEnabled =
        isGlobalFeatureEnabled('mon-copilot-bizchat') &&
        !!isBizchatSidepaneEnabled(targetMailboxInfo) &&
        (hasAISKU || hasStarterSKUAccess || isConsumerWithAI);

    // Don't support shared or delegate mailboxes
    const isSharedOrDelegateMailbox = isMailboxSharedOrDelegateUserMailbox(targetMailboxInfo);

    // Don't support archive mailboxes
    const isArchiveMailbox = targetMailboxInfo.type === 'ArchiveMailbox';

    // Check required search feature flags
    const hasSearchFeatureFlags =
        isGlobalFeatureEnabled('sea-searchPlusChat-noSERPUpdate') ||
        isCopilotSearchAnswerEnabled(targetMailboxInfo);

    return (
        isChatAndAIEnabled &&
        !isSharedOrDelegateMailbox &&
        !isArchiveMailbox &&
        hasSearchFeatureFlags
    );
}
